/**
 * Sentry initialisation for the PriceQ frontend.
 *
 * No-ops when VITE_SENTRY_DSN is unset so local dev stays quiet. Mirrors
 * backend/observability.py so swapping providers later only touches one
 * file per side.
 */

import * as Sentry from '@sentry/react';

/** Exported for the test that pins it: this is the privacy line, not tuning. */
export const SENTRY_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpHeaders: false,
  httpBodies: [],
  urlQueryParams: false,
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  stackFrameVariables: false,
} satisfies NonNullable<Parameters<typeof Sentry.init>[0]>['dataCollection'];

export function initSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    return false;
  }

  Sentry.init({
    dsn,
    // Prefixed to match backend/observability.py: PriceQ shares one Sentry
    // project with the rest of the platform, so `priceq-production` in the
    // environment dropdown is what separates our errors from everyone else's.
    environment: `priceq-${(import.meta.env.MODE as string | undefined) ?? 'development'}`,
    // Which build is actually running. Mirrors backend/observability.py, which
    // reads the same commit from its own GIT_SHA build-arg, so a backend issue
    // and a frontend issue from one deploy carry the same release. Vite inlines
    // this at BUILD time (frontend/Dockerfile promotes the GIT_SHA build-arg to
    // VITE_GIT_SHA); 'unknown' for a local build, which never has one.
    release: (import.meta.env.VITE_GIT_SHA as string | undefined) || 'unknown',
    // Per-route trace sampling. Bump in dev for debug, lower in prod for cost.
    tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.1,
    // Don't capture user input as it may contain tenant data. Sentry 11
    // replaced `sendDefaultPii: false` with per-category switches that all
    // default to ON, so every category that can carry tenant data is named
    // and switched off here; dropping the old flag alone would have started
    // sending cookies, headers, bodies and query strings.
    dataCollection: SENTRY_DATA_COLLECTION,
  });
  return true;
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
